/**
 * VehicleTrackList — Professional Live Fleet Tracking
 * White sidebar · Google Maps full-height · Clean minimal design
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box, Typography, IconButton, CircularProgress,
  TextField, InputAdornment, Divider, Tooltip,
} from '@mui/material';
import { Refresh, Search, LocationOn, GpsOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { InvokeApi, apiList } from '../services/ApiService';
import { Config } from '../config/Config';
import vehicleImg from '../assets/vehicle.png';

const GMAP_KEY = 'AIzaSyBEkY7qnLR8cl0P2_dhmy5ytsZTxCTG67M';

const fmtSec = (s) => {
  if (!s || s <= 0) return null;
  const m = Math.floor(s / 60), h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60 > 0 ? ' ' + (m % 60) + 'm' : ''}`.trim();
  return `${m}m`;
};

const fmtAgo = (d) => {
  if (!d) return null;
  const df = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (df < 60)   return `${df}s ago`;
  if (df < 3600) return `${Math.floor(df / 60)}m ago`;
  return `${Math.floor(df / 3600)}h ago`;
};

// ── Tab pill ──────────────────────────────────────────────────────────────────
const TabPill = ({ id, label, count, active, onClick }) => (
  <Box onClick={() => onClick(id)} sx={{
    display: 'flex', alignItems: 'center', gap: 0.5,
    px: 1.5, py: 0.55, borderRadius: 99, cursor: 'pointer',
    bgcolor: active ? '#1a237e' : '#f1f5f9',
    border: `1.5px solid ${active ? '#1a237e' : '#e2e8f0'}`,
    transition: 'all 0.14s',
    '&:hover': { bgcolor: active ? '#1a237e' : '#e8eaf6' },
  }}>
    <Typography fontSize={11} fontWeight={700}
      sx={{ color: active ? '#fff' : '#64748b' }}>
      {label}
    </Typography>
    <Box sx={{
      minWidth: 18, height: 18, borderRadius: 99,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: active ? 'rgba(255,255,255,0.22)' : '#e2e8f0',
    }}>
      <Typography fontSize={9} fontWeight={800}
        sx={{ color: active ? '#fff' : '#94a3b8' }}>
        {count}
      </Typography>
    </Box>
  </Box>
);

export default function VehicleTrackList() {
  const navigate   = useNavigate();
  const mapRef     = useRef(null);
  const gMapRef    = useRef(null);
  const markersRef = useRef({});
  const timerRef   = useRef(null);

  const [vehicles,   setVehicles]   = useState([]);
  const [telematics, setTelematics] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [mapLoaded,  setMapLoaded]  = useState(false);
  const [search,     setSearch]     = useState('');
  const [tab,        setTab]        = useState('all');
  const [auto,       setAuto]       = useState(true);

  const loadVeh = useCallback(async () => {
    const r = await InvokeApi(Config.base_url + apiList.loconavVehicles, { page: 1, per_page: 200 });
    if (r?.code === '200') setVehicles(r.vehicles || []);
  }, []);

  const loadTel = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    const r = await InvokeApi(Config.base_url + apiList.loconavTelematics, {});
    if (r?.code === '200') setTelematics(r.telematics || []);
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => { loadVeh(); loadTel(); }, [loadVeh, loadTel]);

  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (auto) timerRef.current = setInterval(() => loadTel(true), 30000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [auto, loadTel]);

  useEffect(() => {
    if (window.google?.maps) { setMapLoaded(true); return; }
    const s   = document.createElement('script');
    s.src     = `https://maps.googleapis.com/maps/api/js?key=${GMAP_KEY}`;
    s.async   = true;
    s.onload  = () => setMapLoaded(true);
    document.head.appendChild(s);
  }, []);

  // Merged vehicle + telematics
  const merged = vehicles.map(v => {
    const t = telematics.find(t => t.vehicle_number === v.number) || {};
    return {
      uuid:         v.vehicleUuid,
      number:       v.number,
      lat:          t.lat          ?? null,
      lng:          t.lng          ?? null,
      speed:        t.speed        ?? 0,
      ignition:     t.ignition     ?? 'OFF',
      movement:     t.movement     ?? 'UNKNOWN',
      stopped_sec:  t.stopped_sec  ?? null,
      last_updated: t.last_updated || null,
      assigned_user:v.assigned_user || null,
    };
  });

  const moving  = merged.filter(v => v.movement === 'MOVING').length;
  const stopped = merged.filter(v => v.movement !== 'MOVING' && v.lat).length;
  const noGps   = merged.filter(v => !v.lat).length;

  const filtered = merged.filter(v => {
    const q = search.toLowerCase();
    const matchQ = !q || v.number?.toLowerCase().includes(q) || (v.assigned_user || '').toLowerCase().includes(q);
    const matchT =
      tab === 'moving'  ? v.movement === 'MOVING'  :
      tab === 'stopped' ? (v.movement !== 'MOVING' && !!v.lat) : true;
    return matchQ && matchT;
  });

  // Google Maps markers
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !window.google) return;

    if (!gMapRef.current) {
      gMapRef.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: 12.97, lng: 77.59 },
        zoom: 11,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        styles: [
          { featureType: 'poi',     elementType: 'labels', stylers: [{ visibility: 'off' }] },
          { featureType: 'transit',                        stylers: [{ visibility: 'off' }] },
        ],
      });
    }

    merged.forEach(v => {
      if (!v.lat || !v.lng || !isFinite(v.lat) || !isFinite(v.lng)) return;
      const isMoving = v.movement === 'MOVING';
      const icon = {
        url:        vehicleImg,
        scaledSize: new window.google.maps.Size(20, 46),
        anchor:     new window.google.maps.Point(10, 23),
      };

      if (markersRef.current[v.number]) {
        markersRef.current[v.number].setPosition({ lat: v.lat, lng: v.lng });
      } else {
        const m = new window.google.maps.Marker({
          position: { lat: v.lat, lng: v.lng },
          map:      gMapRef.current,
          title:    v.number,
          icon,
          label: {
            text:      v.number,
            fontSize:  '10px',
            fontWeight:'700',
            color:     isMoving ? '#15803d' : '#b91c1c',
            className: 'gmap-label',
          },
          zIndex: isMoving ? 10 : 5,
        });
        m.addListener('click', () => navigate(`/vehicle-track/${v.uuid}?num=${v.number}`));
        markersRef.current[v.number] = m;
      }
    });

    if (!loading) {
      const pts = merged.filter(v => v.lat != null && v.lng != null && isFinite(v.lat) && isFinite(v.lng) && v.lat !== 0 && v.lng !== 0);
      if (pts.length > 0) {
        const b = new window.google.maps.LatLngBounds();
        pts.forEach(v => b.extend(new window.google.maps.LatLng(parseFloat(v.lat), parseFloat(v.lng))));
        gMapRef.current.fitBounds(b, 40);
      }
    }
  }, [mapLoaded, telematics, loading, navigate]);

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 66px)', overflow: 'hidden', bgcolor: '#f8fafc' }}>

      {/* ── Sidebar ───────────────────────────────────────────────────────── */}
      <Box sx={{
        width: 340, flexShrink: 0,
        display: 'flex', flexDirection: 'column',
        bgcolor: '#fff',
        borderRight: '1px solid #e2e8f0',
        height: '100%', overflow: 'hidden',
        boxShadow: '2px 0 10px rgba(0,0,0,0.04)',
      }}>

        {/* Header */}
        <Box sx={{
          px: 2, pt: 2.5, pb: 2,
          background: 'linear-gradient(135deg, #1a237e 0%, #1565c0 100%)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography fontWeight={800} fontSize={17} sx={{ color: '#fff', mb: 0.3 }}>
                Live Tracking
              </Typography>
              <Typography fontSize={11} sx={{ color: 'rgba(255,255,255,0.6)' }}>
                {merged.length} vehicles · real-time positions
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
              {/* Live/Paused toggle */}
              <Box onClick={() => setAuto(a => !a)} sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                px: 1.1, py: 0.45, borderRadius: 99, cursor: 'pointer',
                bgcolor: auto ? 'rgba(134,239,172,0.18)' : 'rgba(255,255,255,0.1)',
                border: `1px solid ${auto ? 'rgba(134,239,172,0.45)' : 'rgba(255,255,255,0.2)'}`,
              }}>
                <Box sx={{
                  width: 6, height: 6, borderRadius: '50%',
                  bgcolor: auto ? '#86efac' : 'rgba(255,255,255,0.35)',
                  animation: auto ? 'livePulse 1.5s ease-in-out infinite' : 'none',
                }} />
                <Typography fontSize={10} fontWeight={800} letterSpacing={0.6}
                  sx={{ color: auto ? '#86efac' : 'rgba(255,255,255,0.45)' }}>
                  {auto ? 'LIVE' : 'OFF'}
                </Typography>
              </Box>

              <Tooltip title="Refresh">
                <IconButton size="small" onClick={() => loadTel(true)} sx={{
                  width: 30, height: 30, bgcolor: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)', borderRadius: 1.5,
                  color: 'rgba(255,255,255,0.7)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' },
                }}>
                  <Refresh sx={{ fontSize: 15, animation: refreshing ? 'spin 0.8s linear infinite' : 'none' }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Stats strip */}
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            {[
              { label: 'Moving',  val: moving,  color: '#bbf7d0', text: '#fff' },
              { label: 'Stopped', val: stopped, color: 'rgba(255,255,255,0.25)', text: '#fff' },
              { label: 'No GPS',  val: noGps,   color: 'rgba(255,255,255,0.12)', text: 'rgba(255,255,255,0.5)' },
            ].map(s => (
              <Box key={s.label} sx={{
                flex: 1, textAlign: 'center',
                bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 2, py: 1,
              }}>
                <Typography fontWeight={800} fontSize={20} sx={{ color: '#fff', lineHeight: 1.1 }}>
                  {s.val}
                </Typography>
                <Typography fontSize={10} sx={{ color: 'rgba(255,255,255,0.52)', mt: 0.3 }}>
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Search */}
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <TextField fullWidth size="small"
            placeholder="Search plate or rider…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 16, color: '#94a3b8' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2, bgcolor: '#f8fafc', fontSize: 13,
                '& fieldset': { borderColor: '#e2e8f0' },
                '&:hover fieldset': { borderColor: '#c7d2fe' },
                '&.Mui-focused fieldset': { borderColor: '#1a237e' },
              },
            }}
          />
        </Box>

        {/* Filter tabs */}
        <Box sx={{ px: 2, pb: 1.5, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          <TabPill id="all"     label="All"     count={merged.length} active={tab === 'all'}     onClick={setTab} />
          <TabPill id="moving"  label="Moving"  count={moving}        active={tab === 'moving'}  onClick={setTab} />
          <TabPill id="stopped" label="Stopped" count={stopped}       active={tab === 'stopped'} onClick={setTab} />
        </Box>

        <Divider sx={{ borderColor: '#f1f5f9' }} />

        {/* Vehicle list */}
        <Box sx={{
          flex: 1, overflowY: 'auto',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: '#e2e8f0', borderRadius: 2 },
        }}>
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 8, gap: 1.5 }}>
              <CircularProgress size={28} sx={{ color: '#1a237e' }} />
              <Typography fontSize={12} color="text.secondary">Loading vehicles…</Typography>
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ textAlign: 'center', pt: 8, px: 3 }}>
              <Typography fontSize={13} color="text.disabled">No vehicles found</Typography>
            </Box>
          ) : filtered.map(v => {
            const isMoving  = v.movement === 'MOVING';
            const hasGPS    = !!(v.lat && v.lng);
            const dur       = fmtSec(v.stopped_sec);
            const ago       = fmtAgo(v.last_updated);
            const dotColor  = isMoving ? '#16a34a' : '#dc2626';

            return (
              <Box key={v.number}
                onClick={() => navigate(`/vehicle-track/${v.uuid}?num=${v.number}`)}
                sx={{
                  px: 2, py: 1.75, cursor: 'pointer',
                  borderBottom: '1px solid #f1f5f9',
                  borderLeft: `3px solid ${isMoving ? '#16a34a' : 'transparent'}`,
                  transition: 'all 0.12s',
                  '&:hover': {
                    bgcolor: '#f8fafc',
                    borderLeftColor: isMoving ? '#16a34a' : '#94a3b8',
                  },
                }}
              >
                {/* Row 1: plate + ago */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      width: 8, height: 8, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0,
                      boxShadow: isMoving ? `0 0 0 3px ${dotColor}28` : 'none',
                    }} />
                    <Typography fontWeight={800} fontSize={13.5}
                      sx={{ color: '#0f172a', fontFamily: 'monospace', letterSpacing: 0.4 }}>
                      {v.number}
                    </Typography>
                  </Box>
                  {ago && (
                    <Typography fontSize={10} sx={{ color: '#94a3b8', fontWeight: 500 }}>{ago}</Typography>
                  )}
                </Box>

                {/* Row 2: status */}
                <Typography fontSize={11.5} fontWeight={700} sx={{ color: dotColor, mb: 0.75 }}>
                  {isMoving ? `Moving  ·  ${v.speed} km/h` : `Stopped${dur ? '  ·  ' + dur : ''}`}
                </Typography>

                {/* Row 3: GPS */}
                {hasGPS ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.6 }}>
                    <LocationOn sx={{ fontSize: 12, color: '#94a3b8', flexShrink: 0 }} />
                    <Typography fontSize={11} sx={{ color: '#94a3b8' }} noWrap>
                      {v.lat?.toFixed(5)},  {v.lng?.toFixed(5)}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.6 }}>
                    <GpsOff sx={{ fontSize: 12, color: '#cbd5e1' }} />
                    <Typography fontSize={11} sx={{ color: '#cbd5e1' }}>No GPS signal</Typography>
                  </Box>
                )}

                {/* Rider */}
                {v.assigned_user && (
                  <Typography fontSize={11} sx={{ color: '#64748b', mb: 0.75 }}>
                    ↳ {v.assigned_user}
                  </Typography>
                )}

                {/* Chips: IGN + Speed */}
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                  {[
                    { label: 'IGN',   val: v.ignition },
                    { label: 'km/h',  val: v.speed    },
                  ].map(c => (
                    <Box key={c.label} sx={{
                      px: 1.25, py: 0.5,
                      bgcolor: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: 1.5,
                      display: 'flex', flexDirection: 'row', alignItems: 'baseline', gap: 0.5,
                    }}>
                      <Typography fontSize={12} fontWeight={700} sx={{ color: '#374151' }}>{c.val}</Typography>
                      <Typography fontSize={10} sx={{ color: '#94a3b8' }}>{c.label}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Footer */}
        <Box sx={{
          px: 2, py: 1.25,
          borderTop: '1px solid #f1f5f9',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <Typography fontSize={11} sx={{ color: '#94a3b8' }}>
            Showing {filtered.length} of {merged.length}
          </Typography>
          <Typography fontSize={10} sx={{ color: '#cbd5e1' }}>
            {auto ? 'Auto-refresh: 30s' : 'Auto-refresh: off'}
          </Typography>
        </Box>
      </Box>

      {/* ── Google Maps ───────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, height: '100%', position: 'relative' }}>
        <Box ref={mapRef} sx={{ width: '100%', height: '100%' }}>
          {!mapLoaded && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', height: '100%', bgcolor: '#e8eaf0', gap: 2 }}>
              <CircularProgress sx={{ color: '#1a237e' }} />
              <Typography fontSize={12} color="text.secondary">Loading map…</Typography>
            </Box>
          )}
        </Box>

        {/* Map legend */}
        {mapLoaded && (
          <Box sx={{
            position: 'absolute', bottom: 28, right: 12,
            bgcolor: 'rgba(255,255,255,0.97)',
            backdropFilter: 'blur(4px)',
            border: '1px solid #e2e8f0',
            borderRadius: 2,
            px: 2, py: 1.5,
            boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
          }}>
            <Typography fontSize={10} fontWeight={800} sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.7, mb: 0.75 }}>
              Map Legend
            </Typography>
            {[
              { color: '#16a34a', label: 'Moving' },
              { color: '#dc2626', label: 'Stopped' },
            ].map(l => (
              <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: l.color }} />
                <Typography fontSize={11} sx={{ color: '#64748b' }}>{l.label}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <style>{`
        @keyframes spin     { from { transform: rotate(0deg);   } to { transform: rotate(360deg); } }
        @keyframes livePulse { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
        .gmap-label {
          background: rgba(255,255,255,0.94) !important;
          padding: 2px 5px !important;
          border-radius: 4px !important;
          font-size: 10px !important;
          white-space: nowrap !important;
          box-shadow: 0 1px 4px rgba(0,0,0,.15) !important;
          font-family: monospace !important;
          letter-spacing: 0.3px !important;
        }
      `}</style>
    </Box>
  );
}
